import { defineStore } from "pinia";
import { computed, ref } from "vue";

import type { IdMap } from "@/model/baseTypes";
import type { HistoryEntry, SelectionState } from "@/model/selection";
import { asReadonly } from "@/utils/vue";

import { useBoardStore } from "./board";
import { useBoardsStore } from "./boards";
import { useCardStore } from "./card";

type SelectMode = "no" | "hover" | "click" | "rect";

export const useSelectionStore = defineStore("selection", () => {
  const selecting = ref<SelectMode>("no");
  const historyPerBoard = ref<IdMap<Array<HistoryEntry<SelectionState>>>>({});

  const history = computed(() => {
    const boardId = useBoardStore().currentBoard().id;
    if (!(boardId in historyPerBoard.value)) {
      historyPerBoard.value[boardId] = [];
    }
    return historyPerBoard.value[boardId];
  });

  const lastHistoryEntry = computed(() => history.value[history.value.length - 1]);

  function checkUndo(boardId = useBoardStore().currentBoard().id) {
    const boardHistory = historyPerBoard.value[boardId];
    const entry = boardHistory?.[boardHistory.length - 1];
    if (entry?.action.stateChanged(entry.stateAfter)) {
      historyPerBoard.value[boardId] = [];
    }
  }

  function undo() {
    const boardId = useBoardStore().currentBoard().id;
    if (history.value.length > 0) {
      const [historyEntry] = history.value.splice(-1);
      const reset = historyEntry.action.resetToState(historyEntry.stateBefore);
      return (reset || Promise.resolve()).then(() => checkUndo(boardId));
    }
  }

  return {
    selecting: asReadonly(selecting),

    lastHistoryEntry,
    mirrorTargets: computed(() => {
      const cards = useBoardStore().selectedStickyIds.map((id) => useCardStore().cards[id]);
      return useBoardsStore().mirrorTargetBoards(cards);
    }),
    isSelectingWithRect: computed(() => selecting.value === "rect"),

    setSelecting: (mode: SelectMode) => (selecting.value = mode),
    addHistory: <T extends SelectionState>(entry: HistoryEntry<T>) =>
      history.value.push(entry as unknown as HistoryEntry<SelectionState>),
    checkUndo,
    undo,
  };
});
